import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { NewSecretForm, type NewSecretFormOnSubmitInput } from "@/components/secrets/NewSecretForm";
import { SecretLabels } from "@/components/secrets/secret-labels";
import { TrackLocations } from "@/hooks/use-segment";
import { useCreateServiceAccountSecret } from "@/queries/service-account-secret-queries";
import { formatError } from "@/utils/errors";
import { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useState, type FC } from "react";
import type { ServiceAccountHeaders } from "@/api";

type Props = {
    open: boolean;
    onClose: () => void;
    serviceAccountId: string;
    headers: ServiceAccountHeaders;
};

export const ServiceAccountNewSecretModal: FC<Props> = ({ open, onClose, serviceAccountId, headers }) => {
    const { toast } = useToast();
    const createSecret = useCreateServiceAccountSecret({ headers });

    const scope = new SecretScope({
        scope: {
            value: serviceAccountId,
            case: "serviceAccountId",
        },
    });

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const onSubmit = useCallback(
        async ({ secretName, secretValuePlaintext, mount }: NewSecretFormOnSubmitInput) => {
            try {
                await createSecret.mutateAsync({
                    serviceAccountId,
                    secretName,
                    plaintextValue: secretValuePlaintext,
                    mount,
                });
                onClose();
                toast({
                    title: "Secret successfully created",
                    description: SecretLabels.serviceAccount.newSecretModalCreatedToast,
                });
            } catch (e) {
                toast({
                    title: "Sorry, we couldn't create your secret",
                    description: formatError(e),
                });
            }
        },
        [serviceAccountId, createSecret, onClose, toast],
    );

    const [disabled, setDisabled] = useState<boolean>(true);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="service-account-new-secret-modal-content"
                data-track-location={TrackLocations.NewSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>New secret</DialogTitle>
                    <DialogDescription>{SecretLabels.serviceAccount.newSecretModalDescription}</DialogDescription>
                </DialogHeader>

                <DialogBody className="overflow-x max-w-full">
                    <ErrorMessage
                        error={createSecret.error}
                        data-testid="error-message-service-account-secret-creation-failed"
                        className="text-center"
                    />
                    <NewSecretForm
                        formName="service-account-new-secret-form"
                        scope={scope}
                        enableSecretContainerRegistry={true}
                        onSubmit={onSubmit}
                        onDisabledChange={setDisabled}
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="primary"
                        form="service-account-new-secret-form"
                        loading={createSecret.isPending}
                        disabled={createSecret.isPending || disabled}
                        data-testid="service-account-add-secret-submit-button"
                        data-tracking-id="confirm-add-secret-service-account-new-secret-modal"
                    >
                        Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
